var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback, useEffect } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { PageContainer, PageContent } from "../../components/wraper";
import { PrimaryGrid } from "../../components/grid/PrimaryGrid";
import { GridFilters } from "../../components/grid/GridFilters";
import PageHeader from "@ui/components/header/PageHeader";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { TemperatureStatusBadge, getNodeTemperatureStatus, getNodeTemperatureColor, } from "@ui/components/temperature";
import { StatusIndicator } from "@ui/components/status";
import { SButton } from "@ui/components/buttons/SButton";
import { useNodesFilter } from "./hooks/useNodesFilter";
import { useNodesActions } from "./hooks/useNodesActions";
import { useNodesFilterDropdowns } from "./hooks/useNodesFilterDropdowns";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { colCenter } from "@ui/components/style/style";
import { SIconButton } from "@ui/components/buttons";
import { Icon } from "@iconify/react";
import { FreeRoamingBadge } from "@ui/components/badges/FreeRoamingBadge";
import { useAppDispatch } from "@application/store/useStore";
import { updateFilters } from "@reducers/nodes/slice/nodes-slice";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { NodeActionsDialog } from "../dashboard/ap-details/components/NodeActionsDialog";
import { forceDisconnectNode, } from "@reducers/nodes/thunk/nodes-thunk";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { NodeCard } from "./components/NodeCard";
const NodesPage = () => {
    var _a, _b;
    const { t } = useTranslation(["form", "validation", "common"]);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    // Note: SignalR for temperature updates is now handled globally in App.tsx via useSignalR hook
    // Dialog state for disconnect confirmation
    const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    // Delete node dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [nodeToDelete, setNodeToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    // Use existing custom hooks
    const { nodes, loading, searchTerm, filters, paginationInfo, currentPageNumber, currentPageSize, handleFilterChange, handleSearchChange, handleToggleActive, handleClearFilters, handlePageChange, handlePageSizeChange, fetchNodes, } = useNodesFilter();
    // Handle URL query parameters (e.g., ?accessPointUid=xxx)
    useEffect(() => {
        const accessPointUid = searchParams.get("accessPointUid");
        if (accessPointUid) {
            // Set the filter for the access point
            dispatch(updateFilters({ accessPointUid: [accessPointUid] }));
            // Remove the query parameter from URL to keep it clean
            searchParams.delete("accessPointUid");
            navigate({ search: searchParams.toString() }, { replace: true });
        }
    }, [searchParams, dispatch, navigate]);
    // Use filter dropdowns hook
    const { options: dropdownOptions, loading: dropdownLoading, searchNodeUids, searchNodeNames, searchApUids, searchApNames, loadInitialOptions, } = useNodesFilterDropdowns();
    const { handleAddNode, handleEditNode, handleDisconnectNode, handleDeleteNode, handleConfigureAPDevice, } = useNodesActions();
    // Handle dropdown input change with debounce
    const handleDropdownInputChange = useCallback((field, inputValue) => {
        switch (field) {
            case "nodeUid":
                searchNodeUids(inputValue);
                break;
            case "nodeName":
                searchNodeNames(inputValue);
                break;
            case "accessPointUid":
                searchApUids(inputValue);
                break;
            case "accessPointName":
                searchApNames(inputValue);
                break;
        }
    }, [searchNodeUids, searchNodeNames, searchApUids, searchApNames]);
    // Helper to get filter options with selected values included
    const getFilterOptionsWithSelected = useCallback((searchResults, selectedValues) => {
        const searchOptions = searchResults.map((item) => ({
            value: item,
            label: item,
        }));
        // Add selected values that aren't in search results
        if (selectedValues) {
            const selectedArray = Array.isArray(selectedValues)
                ? selectedValues
                : [selectedValues];
            const existingValues = new Set(searchResults);
            const selectedOptions = selectedArray
                .filter((val) => !existingValues.has(val))
                .map((val) => ({ value: val, label: val }));
            return [...selectedOptions, ...searchOptions];
        }
        return searchOptions;
    }, []);
    // Simple menu handlers
    const columns = [
        {
            field: "node",
            headerName: t("common:grid.node"),
            minWidth: 100,
            filterable: false,
            renderCell: (params) => {
                var _a;
                return (React.createElement(Box, { sx: Object.assign({ height: "100%" }, colCenter) },
                    React.createElement(TemperatureDisplay, Object.assign({ width: 91, temperature: (_a = params.row.nodeTemperature) !== null && _a !== void 0 ? _a : null }, (params.row.nodeTypeCode === 1
                        ? { leftIndicator: 1, rightIndicator: 2 }
                        : {}), { color: getNodeTemperatureColor(params.row) }))));
            },
        },
        {
            field: "nodeId",
            headerName: t("common:grid.nodeUid"),
            minWidth: 100,
            filterable: true,
            filterType: "asyncSelect",
            filterField: "nodeUid", // Backend filter field name
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.nodeUids, filters.nodeUid),
            filterLoading: dropdownLoading.nodeUids,
            onFilterInputChange: (value) => handleDropdownInputChange("nodeUid", value),
        },
        {
            field: "nodeName",
            headerName: t("common:grid.nodeName"),
            flex: 1.5,
            minWidth: 120,
            filterable: true,
            filterType: "asyncSelect",
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.nodeNames, filters.nodeName),
            filterLoading: dropdownLoading.nodeNames,
            onFilterInputChange: (value) => handleDropdownInputChange("nodeName", value),
            // Free roaming nodes stay in this grid; the badge marks that their configuration
            // follows the access point physically relaying them.
            renderCell: (params) => {
                var _a;
                return (
                // The name takes the leftover width so the badge always sits at the same
                // x position row to row, instead of drifting with each name's length.
                React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { height: "100%", width: "100%", minWidth: 0 } },
                    React.createElement(Box, { sx: { flex: 1, minWidth: 0 } },
                        React.createElement(Tooltip, { title: (_a = params.row.nodeName) !== null && _a !== void 0 ? _a : "" },
                            React.createElement(Typography, { variant: "body2", noWrap: true, sx: { overflow: "hidden", textOverflow: "ellipsis" } }, params.row.nodeName))),
                    params.row.freeRoamingNode && (React.createElement(Box, { sx: { flexShrink: 0 } },
                        React.createElement(FreeRoamingBadge, null)))));
            },
        },
        {
            field: "temperature",
            headerName: t("common:grid.temperature"),
            minWidth: 100,
            filterable: false,
            renderCell: (params) => (React.createElement(Box, { sx: Object.assign({ height: "100%" }, colCenter) },
                React.createElement(TemperatureStatusBadge, { status: getNodeTemperatureStatus(params.row) }))),
        },
        {
            field: "status",
            headerName: t("common:grid.status"),
            flex: 1,
            minWidth: 100,
            filterable: true,
            filterType: "select",
            filterOptions: ["active", "inactive"],
            renderCell: (params) => (React.createElement(StatusIndicator, { variant: params.row.isActive ? "active" : "inactive" })),
        },
        {
            field: "deviceId",
            headerName: t("common:grid.apId"),
            flex: 1,
            minWidth: 80,
            filterable: true,
            filterType: "asyncSelect",
            filterField: "accessPointUid", // Backend filter field name
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.apUids, filters.accessPointUid),
            filterLoading: dropdownLoading.apUids,
            onFilterInputChange: (value) => handleDropdownInputChange("accessPointUid", value),
        },
        {
            field: "deviceName",
            headerName: t("common:grid.apName"),
            flex: 1.5,
            minWidth: 100,
            filterable: true,
            filterType: "asyncSelect",
            filterField: "accessPointName", // Backend filter field name
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.apNames, filters.accessPointName),
            filterLoading: dropdownLoading.apNames,
            onFilterInputChange: (value) => handleDropdownInputChange("accessPointName", value),
        },
        {
            field: "actions",
            headerName: t("common:grid.actions"),
            // flex: 1.5,
            headerAlign: "center",
            minWidth: 310,
            filterable: false,
            renderCell: (params) => {
                var _a, _b;
                const isAlreadyDisconnected = !((_a = params.row.deviceId) === null || _a === void 0 ? void 0 : _a.trim());
                return (React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center" },
                    PermissionChecker.hasPermission(Permission.ACCESSPOINTS_DELETE) && (React.createElement(SIconButton, { title: isAlreadyDisconnected
                            ? t("common:actions.nodeAlreadyDisconnected")
                            : t("common:actions.disconnectNode"), colorVariant: "error", size: "small", disabled: isAlreadyDisconnected, onClick: () => {
                            setSelectedNode({
                                id: params.row.id,
                                nodeName: params.row.nodeName,
                                nodeId: params.row.nodeId,
                                nodeTemperature: params.row.nodeTemperature,
                                deviceName: params.row.deviceName,
                            });
                            setIsDisconnectDialogOpen(true);
                        } },
                        React.createElement(Icon, { icon: "mingcute:close-line" }))),
                    PermissionChecker.hasPermission(Permission.NODES_DELETE) && (React.createElement(SIconButton, { title: t("common:actions.delete"), colorVariant: "error", size: "small", onClick: () => {
                            setNodeToDelete({
                                id: params.row.id,
                                nodeName: params.row.nodeName,
                                nodeId: params.row.nodeId,
                            });
                            setDeleteDialogOpen(true);
                            setDeleteError(null);
                        } },
                        React.createElement(Icon, { icon: "mingcute:delete-2-line" }))),
                    React.createElement(SButton, { size: "medium", variant: "soft", onClick: () => {
                            var _a;
                            const ap = (_a = params.row.deviceId) === null || _a === void 0 ? void 0 : _a.trim();
                            navigate(ap
                                ? `/nodes/${params.row.nodeId}/logs/${ap}`
                                : `/nodes/${params.row.nodeId}/logs`);
                        } }, t("common:actions.logs")),
                    PermissionChecker.hasPermission(Permission.NODES_EDIT) && (React.createElement(SButton, { variant: "soft", color: "primary", size: "medium", onClick: () => handleEditNode(params.row.id) }, t("common:actions.edit"))),
                    PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT) && (React.createElement(SButton, { variant: "outlined", color: "primary", size: "medium", onClick: () => handleConfigureAPDevice(params.row.deviceId), disabled: !((_b = params.row.deviceId) === null || _b === void 0 ? void 0 : _b.trim()) }, "AP"))));
            },
        },
    ];
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.nodes") }),
        React.createElement(PageContent, null,
            React.createElement(GridFilters, { title: `${(_a = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _a !== void 0 ? _a : nodes.length} ${t("common:pages.nodes")}`, loading: loading, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange, onFilterOpen: loadInitialOptions, onApplyFilters: fetchNodes }),
            React.createElement(PrimaryGrid, { rows: loading ? [] : nodes, columns: columns, loading: loading, heightVariant: "full", checkboxSelection: false, autoHeight: true, paginationMode: "server", renderCard: (row) => (React.createElement(NodeCard, { row: row, onDisconnect: (node) => {
                        setSelectedNode({
                            id: node.id,
                            nodeName: node.nodeName,
                            nodeId: node.nodeId,
                            nodeTemperature: node.nodeTemperature,
                            deviceName: node.deviceName,
                        });
                        setIsDisconnectDialogOpen(true);
                    }, onDelete: (node) => {
                        setNodeToDelete({
                            id: node.id,
                            nodeName: node.nodeName,
                            nodeId: node.nodeId,
                        });
                        setDeleteDialogOpen(true);
                        setDeleteError(null);
                    }, onLogs: (nodeId, deviceId) => {
                        const ap = deviceId === null || deviceId === void 0 ? void 0 : deviceId.trim();
                        navigate(ap
                            ? `/nodes/${nodeId}/logs/${ap}`
                            : `/nodes/${nodeId}/logs`);
                    }, onEdit: (id) => handleEditNode(id), onApConfig: (deviceId) => handleConfigureAPDevice(deviceId), onClick: (deviceId) => {
                        if (deviceId)
                            navigate(`/dashboard/${deviceId}`);
                    } })), rowCount: (_b = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _b !== void 0 ? _b : 0, paginationModel: {
                    page: currentPageNumber - 1,
                    pageSize: currentPageSize,
                }, onPaginationModelChange: (model) => {
                    handlePageChange(model.page + 1);
                    handlePageSizeChange(model.pageSize);
                }, pageSizeOptions: [25, 50], onRowClick: (params, event) => {
                    // Don't navigate if clicking on action buttons or columns
                    const target = event.target;
                    if (target.closest('[data-field="actions"]') ||
                        target.closest("button") ||
                        target.closest('[role="button"]')) {
                        return;
                    }
                    // Navigate to AP Profile page (same as Dashboard)
                    // deviceId is the accessPointUid from the node
                    if (params.row.deviceId) {
                        navigate(`/dashboard/${params.row.deviceId}`);
                    }
                } }),
            React.createElement(NodeActionsDialog, { isOpen: isDisconnectDialogOpen, onClose: () => {
                    setIsDisconnectDialogOpen(false);
                    setSelectedNode(null);
                }, onConfirm: () => __awaiter(void 0, void 0, void 0, function* () {
                    if (!selectedNode)
                        return;
                    setIsDisconnecting(true);
                    try {
                        yield dispatch(forceDisconnectNode({
                            nodeId: selectedNode.id,
                        })).unwrap();
                        setIsDisconnectDialogOpen(false);
                        setSelectedNode(null);
                        setTimeout(() => {
                            fetchNodes();
                        }, 200);
                    }
                    catch (error) {
                        console.error("Failed to disconnect node:", error);
                    }
                    finally {
                        setIsDisconnecting(false);
                    }
                }), selectedNode: selectedNode, action: "disconnect", accessPointName: selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.deviceName }),
            React.createElement(ConfirmDialog, { isOpen: deleteDialogOpen, onClose: () => {
                    if (!deleteLoading) {
                        setDeleteDialogOpen(false);
                        setNodeToDelete(null);
                        setDeleteError(null);
                    }
                }, onConfirm: () => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b;
                    if (!nodeToDelete)
                        return;
                    setDeleteError(null);
                    setDeleteLoading(true);
                    try {
                        yield handleDeleteNode(nodeToDelete.id);
                        setDeleteDialogOpen(false);
                        setNodeToDelete(null);
                        setTimeout(() => fetchNodes(), 200);
                    }
                    catch (err) {
                        setDeleteError((err === null || err === void 0 ? void 0 : err.message) ||
                            ((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) ||
                            "Failed to delete node");
                    }
                    finally {
                        setDeleteLoading(false);
                    }
                }), loading: deleteLoading, variant: "delete", actionText: t("common:actions.delete"), screenName: nodeToDelete
                    ? `${t("common:grid.nodeName")} "${nodeToDelete.nodeName || nodeToDelete.nodeId}"`
                    : "", customMessage: deleteError !== null && deleteError !== void 0 ? deleteError : undefined }))));
};
export default NodesPage;
